import { useState, useCallback, useEffect, useRef } from 'react';
import { appToast } from 'utils/toast.utils';
import {
  ERRORS,
  MULTI_EDIT_FIELDS,
  OBJECT_VALUE_COLUMNS,
  TIMING,
} from '../../TransportCloudTable.consts';
import { GroupedItems } from '../../TransportCloudTable.types';
import { transformCellUpdateForBackend } from '../../utils/cellUpdateTransformer';
import { validateCell } from '../../utils/cellValidation';
import {
  CellPosition,
  SavingCell,
  ValidationError,
  SendCellUpdateFn,
  SendEditingStateFn,
  RecentEdit,
} from './cellEditing.types';
import {
  extractEditingPayload,
  findRowAndOldValue,
  formatDisplayValue,
  normalizeTruckerRate,
  updateGroupedRows,
  valuesAreEqual,
  formatDateOnly,
  getItemId,
  valuesMatchForCorrelation,
  parseDisplayDateTimeToEpoch,
  findEditableSelectedItems,
  buildMultiEditPayload,
} from './cellEditing.utils';
import { useTimeoutManager } from './timeoutManager';

const DRIVER_FIELDS = ['driverName', 'driverDocumentNumber', 'driverPhone'];

const NOTIFY_ONLY_COLUMNS = ['loadingDeliveryDate'];

export const useCellEditing = (
  sendCellUpdate?: SendCellUpdateFn,
  sendStartEditing?: SendEditingStateFn,
  sendStopEditing?: SendEditingStateFn,
  sendCellUpdateNotify?: SendCellUpdateFn,
  groupedRows?: GroupedItems[],
  selectedItems?: string[],
  canEditAllRecords?: boolean,
  hasOwnerEditPrivilege?: boolean,
  canEditAllShipmentPlans?: boolean,
  hasOwnerEditShipmentPlansPrivilege?: boolean,
  ownerIds?: Set<number>,
) => {
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
  const editingValueRef = useRef<any>(null);
  const [savingCells, setSavingCells] = useState<SavingCell[]>([]);
  const [recentlyEditedCell, setRecentlyEditedCell] =
    useState<CellPosition | null>(null);
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);
  const [recentlySavedFields, setRecentlySavedFields] = useState<string[]>([]);
  const [dateChangedRows, setDateChangedRows] = useState<
    Map<string, { newDate: string; status: 'pending' | 'accepted' }>
  >(new Map());
  const [recentEdits, setRecentEdits] = useState<RecentEdit[]>([]);
  const [multiEditConfirmation, setMultiEditConfirmation] = useState<{
    itemId: string;
    columnKey: string;
    newValue: any;
    position: { top: number; left: number };
  } | null>(null);
  const [acceptMultiEditConfirmation, setAcceptMultiEditConfirmation] =
    useState<{
      itemId: string;
      position: { top: number; right: number };
    } | null>(null);

  const validationTimeouts = useTimeoutManager();
  const savedFieldTimeouts = useTimeoutManager();
  const pendingEdits = useTimeoutManager();
  const backendTimeouts = useTimeoutManager();
  const isCleanedUp = useRef(false);
  const groupedRowsRef = useRef<GroupedItems[]>(groupedRows || []);
  const savingCellsRef = useRef<SavingCell[]>([]);
  const recentEditsRef = useRef<RecentEdit[]>([]);
  const recentlySavedFieldsRef = useRef<string[]>([]);
  const skipMultiEditCheckRef = useRef(false);
  const multiEditJustConfirmedRef = useRef<{
    itemId: string;
    columnKey: string;
  } | null>(null);
  const multiEditTimeoutKeys = useRef<Map<string, string>>(new Map());
  const editingCellRef = useRef<CellPosition | null>(null);
  const selectedItemsRef = useRef(selectedItems);
  const multiEditConfirmationRef = useRef(multiEditConfirmation);
  const acceptMultiEditConfirmationRef = useRef(acceptMultiEditConfirmation);
  const dateChangedRowsRef = useRef(dateChangedRows);
  const canEditAllRecordsRef = useRef(canEditAllRecords);
  const hasOwnerEditPrivilegeRef = useRef(hasOwnerEditPrivilege);
  const canEditAllShipmentPlansRef = useRef(canEditAllShipmentPlans);
  const hasOwnerEditShipmentPlansPrivilegeRef = useRef(
    hasOwnerEditShipmentPlansPrivilege,
  );
  const ownerIdsRef = useRef(ownerIds);
  const driverFieldBatchRef = useRef<
    Map<
      string,
      {
        rowId?: number;
        shipmentPlanContainerId?: number;
        fields: Record<string, any>;
      }
    >
  >(new Map());

  groupedRowsRef.current = groupedRows || [];
  savingCellsRef.current = savingCells;
  recentEditsRef.current = recentEdits;
  recentlySavedFieldsRef.current = recentlySavedFields;
  editingCellRef.current = editingCell;
  selectedItemsRef.current = selectedItems;
  multiEditConfirmationRef.current = multiEditConfirmation;
  acceptMultiEditConfirmationRef.current = acceptMultiEditConfirmation;
  dateChangedRowsRef.current = dateChangedRows;
  canEditAllRecordsRef.current = canEditAllRecords;
  hasOwnerEditPrivilegeRef.current = hasOwnerEditPrivilege;
  canEditAllShipmentPlansRef.current = canEditAllShipmentPlans;
  hasOwnerEditShipmentPlansPrivilegeRef.current =
    hasOwnerEditShipmentPlansPrivilege;
  ownerIdsRef.current = ownerIds;

  const setEditingValue = useCallback((val: any) => {
    editingValueRef.current = val;
  }, []);

  const handleCellEdit = useCallback(
    (
      itemId: string,
      columnKey: string,
      currentValue: any,
      onlyIfSwitching = false,
    ) => {
      if (columnKey === 'actions') return;

      const currentEditing = editingCellRef.current;
      const isCurrentCell =
        currentEditing?.itemId === itemId &&
        currentEditing?.columnKey === columnKey;

      if (onlyIfSwitching && isCurrentCell) return;

      if (currentEditing && !isCurrentCell && sendStopEditing) {
        const prevPayload = extractEditingPayload(
          currentEditing.itemId,
          currentEditing.columnKey,
          groupedRowsRef.current,
        );
        if (prevPayload) sendStopEditing(prevPayload);
      }

      isCleanedUp.current = false;
      setEditingCell({ itemId, columnKey });

      const initialValue =
        OBJECT_VALUE_COLUMNS.includes(columnKey) && currentValue
          ? { code: currentValue, id: null }
          : currentValue;

      editingValueRef.current = initialValue;

      if (sendStartEditing) {
        const payload = extractEditingPayload(
          itemId,
          columnKey,
          groupedRowsRef.current,
        );
        if (payload) sendStartEditing(payload);
      }
    },
    [sendStartEditing, sendStopEditing],
  );

  const markFieldAsSaved = useCallback(
    (columnKey: string) => {
      setRecentlySavedFields((prev) =>
        prev.includes(columnKey) ? prev : [...prev, columnKey],
      );

      savedFieldTimeouts.set(
        columnKey,
        () => {
          setRecentlySavedFields((prev) => prev.filter((f) => f !== columnKey));
        },
        TIMING.RECENTLY_EDITED_DURATION,
      );
    },
    [savedFieldTimeouts],
  );

  const markDateChangeAccepted = useCallback((itemId: string) => {
    setDateChangedRows((prev) => {
      const entry = prev.get(itemId);
      if (!entry || entry.status === 'accepted') return prev;
      const newMap = new Map(prev);
      newMap.set(itemId, { ...entry, status: 'accepted' });
      return newMap;
    });
  }, []);

  const showValidationError = useCallback(
    (
      itemId: string,
      columnKey: string,
      errorMessage: string,
      onComplete: () => void,
    ) => {
      if (isCleanedUp.current) {
        return;
      }

      setValidationError({
        itemId,
        columnKey,
        errorMessage,
        status: 'active',
      });

      validationTimeouts.set(
        'validation',
        () => {
          setValidationError((prev) =>
            prev ? { ...prev, status: 'fading' } : null,
          );

          setTimeout(() => {
            setValidationError(null);
            onComplete();
          }, TIMING.STATUS_FADE_OUT_DURATION);
        },
        TIMING.VALIDATION_FADE_START_DELAY,
      );
    },
    [validationTimeouts],
  );

  const updateRowsWithFormattedValue = useCallback(
    (
      setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>,
      itemId: string,
      columnKey: string,
      newValue: any,
    ) => {
      if (columnKey === 'customsClearance') {
        setGroupedRows((prevRows) => {
          let formattedValue = newValue;
          let updatedRows = prevRows;
          for (const group of prevRows) {
            for (const jobCodeGroup of group.jobCodeGroups) {
              const item = jobCodeGroup.items.find(
                (i) => getItemId(i) === itemId,
              );
              if (item) {
                formattedValue = formatDisplayValue(
                  newValue,
                  columnKey,
                  item.tradeType,
                );
                break;
              }
            }
            if (formattedValue !== newValue) break;
          }
          updatedRows = updateGroupedRows(
            prevRows,
            itemId,
            columnKey,
            formattedValue,
          );

          if (newValue && typeof newValue === 'object') {
            updatedRows = updatedRows.map((group) => ({
              ...group,
              jobCodeGroups: group.jobCodeGroups.map((jobCodeGroup) => ({
                ...jobCodeGroup,
                items: jobCodeGroup.items.map((item) =>
                  getItemId(item) === itemId
                    ? {
                        ...item,
                        customsClearancePlaceId:
                          newValue.placeId !== undefined
                            ? newValue.placeId
                            : item.customsClearancePlaceId,
                        customsClearancePlaceCode:
                          newValue.placeCode !== undefined
                            ? newValue.placeCode
                            : item.customsClearancePlaceCode,
                      }
                    : item,
                ),
              })),
            }));
          }

          return updatedRows;
        });
      } else {
        const valueToStore =
          columnKey === 'truckerRate'
            ? newValue
            : formatDisplayValue(newValue, columnKey);

        setGroupedRows((prevRows) =>
          updateGroupedRows(prevRows, itemId, columnKey, valueToStore),
        );
      }
    },
    [],
  );

  const fadeAndRemoveSavingCells = useCallback(
    (itemIds: string[], columnKey: string) => {
      setTimeout(() => {
        setSavingCells((current) =>
          current.map((cell) =>
            itemIds.includes(cell.itemId) && cell.columnKey === columnKey
              ? {
                  ...cell,
                  status:
                    cell.status === 'success-other'
                      ? ('fading-other' as const)
                      : ('fading' as const),
                }
              : cell,
          ),
        );

        setTimeout(() => {
          setSavingCells((current) =>
            current.filter(
              (cell) =>
                !(
                  itemIds.includes(cell.itemId) && cell.columnKey === columnKey
                ),
            ),
          );
        }, TIMING.STATUS_FADE_OUT_DURATION);
      }, TIMING.STATUS_FADE_START_DELAY);
    },
    [],
  );

  const scheduleMultiEditFailureTimeout = useCallback(
    (
      timeoutKey: string,
      itemIds: string[],
      columnKey: string,
      onFailure?: () => void,
    ) => {
      backendTimeouts.set(
        timeoutKey,
        () => {
          appToast.error(ERRORS.systemError);
          onFailure?.();

          itemIds.forEach((id) =>
            multiEditTimeoutKeys.current.delete(`${id}-${columnKey}`),
          );

          setSavingCells((prev) =>
            prev.map((cell) =>
              itemIds.includes(cell.itemId) && cell.columnKey === columnKey
                ? { ...cell, status: 'error' as const }
                : cell,
            ),
          );

          fadeAndRemoveSavingCells(itemIds, columnKey);
        },
        TIMING.BACKEND_RESPONSE_TIMEOUT,
      );
    },
    [backendTimeouts, fadeAndRemoveSavingCells],
  );

  const sendUpdateToBackend = useCallback(
    (
      itemId: string,
      columnKey: string,
      backendPayload: any,
      setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>,
      isDriverBatchedSelect = false,
    ) => {
      const timeoutKey = `${itemId}-${columnKey}`;

      backendTimeouts.set(
        timeoutKey,
        () => {
          setSavingCells((prev) => {
            const savingCell = prev.find(
              (cell) =>
                cell.itemId === itemId &&
                cell.columnKey === columnKey &&
                cell.status === 'loading',
            );

            if (savingCell) {
              appToast.error(ERRORS.systemError);

              if (savingCell.oldValue !== undefined) {
                setGroupedRows((prevRows) =>
                  updateGroupedRows(
                    prevRows,
                    itemId,
                    columnKey,
                    savingCell.oldValue,
                  ),
                );
              }

              if (sendStopEditing) {
                const payload = extractEditingPayload(
                  itemId,
                  columnKey,
                  groupedRowsRef.current,
                );
                if (payload) sendStopEditing(payload);
              }

              fadeAndRemoveSavingCells([itemId], columnKey);

              return prev.map((cell) =>
                cell.itemId === itemId && cell.columnKey === columnKey
                  ? { ...cell, status: 'error' as const }
                  : cell,
              );
            }
            return prev;
          });
        },
        TIMING.BACKEND_RESPONSE_TIMEOUT,
      );

      if (sendCellUpdate) {
        if (
          isDriverBatchedSelect &&
          !Array.isArray(backendPayload) &&
          DRIVER_FIELDS.includes(columnKey) &&
          (typeof backendPayload.rowId === 'number' ||
            typeof backendPayload.shipmentPlanContainerId === 'number')
        ) {
          const { rowId, shipmentPlanContainerId, fieldName, newValue } =
            backendPayload;
          const batch = driverFieldBatchRef.current.get(itemId) ?? {
            rowId,
            shipmentPlanContainerId,
            fields: {} as Record<string, any>,
          };
          batch.fields[fieldName] = newValue;
          driverFieldBatchRef.current.set(itemId, batch);

          queueMicrotask(() => {
            const pendingBatch = driverFieldBatchRef.current.get(itemId);
            if (!pendingBatch) return;
            driverFieldBatchRef.current.delete(itemId);
            sendCellUpdate({
              rowId: pendingBatch.rowId,
              shipmentPlanContainerId: pendingBatch.shipmentPlanContainerId,
              fields: pendingBatch.fields,
            });
          });
        } else if (Array.isArray(backendPayload)) {
          backendPayload.forEach((payload) => sendCellUpdate(payload));
        } else {
          sendCellUpdate(backendPayload);
        }
      } else {
        setSavingCells((prev) =>
          prev.filter(
            (cell) => !(cell.itemId === itemId && cell.columnKey === columnKey),
          ),
        );
      }
    },
    [
      backendTimeouts,
      sendCellUpdate,
      sendStopEditing,
      fadeAndRemoveSavingCells,
    ],
  );

  const handleDateChangeNotify = useCallback(
    (
      itemId: string,
      newValue: string,
      setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>,
    ) => {
      setDateChangedRows((prev) => {
        const newMap = new Map(prev);
        newMap.set(itemId, { newDate: newValue, status: 'pending' });
        return newMap;
      });
      updateRowsWithFormattedValue(
        setGroupedRows,
        itemId,
        'loadingDeliveryDate',
        newValue,
      );
    },
    [updateRowsWithFormattedValue],
  );

  const seedDateChangedRowsFromItems = useCallback(
    (
      rows: GroupedItems[],
      setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>,
    ) => {
      const newMap = new Map<
        string,
        { newDate: string; status: 'pending' | 'accepted' }
      >();
      rows.forEach((group) => {
        group.jobCodeGroups.forEach((jobCodeGroup) => {
          jobCodeGroup.items.forEach((item) => {
            const notification = item.notifications?.find(
              (n) => n.type === 'UPDATE' && n.field === 'loadingDeliveryDate',
            );
            if (notification) {
              const id = getItemId(item);
              if (id) {
                newMap.set(id, {
                  newDate: notification.value,
                  status: 'pending',
                });
                updateRowsWithFormattedValue(
                  setGroupedRows,
                  id,
                  'loadingDeliveryDate',
                  notification.value,
                );
              }
            }
          });
        });
      });
      setDateChangedRows(newMap);
    },
    [updateRowsWithFormattedValue],
  );

  const handleAcceptDateChange = useCallback(
    (
      itemId: string,
      currentGroupedRows: GroupedItems[],
      setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>,
      bypassMultiCheck = false,
    ) => {
      const pending = dateChangedRowsRef.current.get(itemId);
      if (!pending || pending.status === 'accepted') return;

      if (!bypassMultiCheck && !skipMultiEditCheckRef.current) {
        const currentSelected = selectedItemsRef.current;
        const hasMultipleSelected =
          currentSelected && currentSelected.length > 1;
        const currentItemIsSelected = currentSelected?.includes(itemId);

        if (hasMultipleSelected && currentItemIsSelected) {
          const acceptableSelectedIds = currentSelected.filter((id) => {
            const entry = dateChangedRowsRef.current.get(id);
            return entry && entry.status === 'pending';
          });

          let hasJobEntryRow = false;
          let hasNonJobEntryRow = false;
          acceptableSelectedIds.forEach((selId) => {
            for (const group of currentGroupedRows) {
              for (const jobCodeGroup of group.jobCodeGroups) {
                const foundItem = jobCodeGroup.items.find(
                  (item) => getItemId(item) === selId,
                );
                if (foundItem) {
                  if (foundItem.jobEntryId) {
                    hasJobEntryRow = true;
                  } else {
                    hasNonJobEntryRow = true;
                  }
                  break;
                }
              }
            }
          });
          const canMultiAccept = !(hasJobEntryRow && hasNonJobEntryRow);

          if (canMultiAccept && acceptableSelectedIds.length > 1) {
            const iconElement = document.querySelector(
              `[data-item-id="${itemId}"][data-warning-icon="dateChange"]`,
            ) as HTMLElement;
            if (iconElement) {
              const rect = iconElement.getBoundingClientRect();
              setAcceptMultiEditConfirmation({
                itemId,
                position: {
                  top: rect.bottom + window.scrollY + 5,
                  right: window.innerWidth - rect.right - window.scrollX,
                },
              });
              return;
            }
          }
        }
      }

      const epochValue = parseDisplayDateTimeToEpoch(pending.newDate);
      if (epochValue === null) return;

      const { rowData, oldValue } = findRowAndOldValue(
        currentGroupedRows,
        itemId,
        'loadingDeliveryDate',
      );

      const backendPayload = transformCellUpdateForBackend(
        {
          itemId,
          columnKey: 'loadingDeliveryDate',
          newValue: epochValue,
          oldValue,
        },
        rowData,
      );

      setSavingCells((prev) => [
        ...prev.filter(
          (cell) =>
            !(
              cell.itemId === itemId && cell.columnKey === 'loadingDeliveryDate'
            ),
        ),
        {
          itemId,
          columnKey: 'loadingDeliveryDate',
          status: 'loading',
          oldValue,
        },
      ]);

      sendUpdateToBackend(
        itemId,
        'loadingDeliveryDate',
        backendPayload,
        setGroupedRows,
      );
    },
    [sendUpdateToBackend],
  );

  const handleAcceptMultiEditConfirm = useCallback(() => {
    const currentAccept = acceptMultiEditConfirmationRef.current;
    const currentSelected = selectedItemsRef.current;
    if (!currentAccept || !currentSelected) return;

    const { itemId } = currentAccept;
    const currentGroupedRows = groupedRowsRef.current;
    const pending = dateChangedRowsRef.current.get(itemId);

    setAcceptMultiEditConfirmation(null);

    if (!pending || pending.status === 'accepted') return;

    const selectedIdsByDate = new Map<number, string[]>();
    currentSelected.forEach((id) => {
      const entry = dateChangedRowsRef.current.get(id);
      if (!entry || entry.status !== 'pending') return;

      const epochValue = parseDisplayDateTimeToEpoch(entry.newDate);
      if (epochValue === null) return;

      const ids = selectedIdsByDate.get(epochValue) ?? [];
      ids.push(id);
      selectedIdsByDate.set(epochValue, ids);
    });

    selectedIdsByDate.forEach((selectedIds, epochValue) => {
      const { jobEntryIds, shipmentPlanContainerIds, editableItemIds } =
        findEditableSelectedItems(currentGroupedRows, selectedIds);
      if (editableItemIds.length === 0) return;

      const multiPayload = buildMultiEditPayload(
        { fieldName: 'loadingDeliveryDate', newValue: epochValue },
        jobEntryIds,
        shipmentPlanContainerIds,
      );

      setSavingCells((prev) => [
        ...prev.filter(
          (cell) =>
            !editableItemIds.includes(cell.itemId) ||
            cell.columnKey !== 'loadingDeliveryDate',
        ),
        ...editableItemIds.map((selectedId) => ({
          itemId: selectedId,
          columnKey: 'loadingDeliveryDate',
          status: 'loading' as const,
          oldValue: findRowAndOldValue(
            currentGroupedRows,
            selectedId,
            'loadingDeliveryDate',
          ).oldValue,
        })),
      ]);

      const multiTimeoutKey = `accept-multi-${itemId}-loadingDeliveryDate-${epochValue}`;
      editableItemIds.forEach((selectedId) => {
        multiEditTimeoutKeys.current.set(
          `${selectedId}-loadingDeliveryDate`,
          multiTimeoutKey,
        );
      });

      scheduleMultiEditFailureTimeout(
        multiTimeoutKey,
        editableItemIds,
        'loadingDeliveryDate',
      );

      sendCellUpdate?.(multiPayload);
    });
  }, [sendCellUpdate, scheduleMultiEditFailureTimeout]);

  const handleAcceptMultiEditCancel = useCallback(
    (setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>) => {
      const currentAccept = acceptMultiEditConfirmationRef.current;
      if (!currentAccept) return;

      setAcceptMultiEditConfirmation(null);
      handleAcceptDateChange(
        currentAccept.itemId,
        groupedRowsRef.current,
        setGroupedRows,
        true,
      );
    },
    [handleAcceptDateChange],
  );

  const handleAcceptMultiEditClickOutside = useCallback(() => {
    setAcceptMultiEditConfirmation(null);
  }, []);

  const markCellAsEdited = useCallback((itemId: string, columnKey: string) => {
    setRecentlyEditedCell({ itemId, columnKey });
    setTimeout(() => {
      setRecentlyEditedCell(null);
    }, TIMING.RECENTLY_EDITED_DURATION);
  }, []);

  const handleCellSave = useCallback(
    (
      itemId: string,
      columnKey: string,
      newValue: any,
      currentGroupedRows: GroupedItems[],
      setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>,
      bypassMultiEditCheck = false,
      isDriverBatchedSelect = false,
    ) => {
      if (
        multiEditJustConfirmedRef.current?.itemId === itemId &&
        multiEditJustConfirmedRef.current?.columnKey === columnKey
      ) {
        return;
      }

      validationTimeouts.clear('validation');

      const { rowData, oldValue } = findRowAndOldValue(
        currentGroupedRows,
        itemId,
        columnKey,
      );

      const validation = validateCell(newValue, columnKey, rowData);
      if (!validation.isValid) {
        showValidationError(
          itemId,
          columnKey,
          validation.errorMessage || 'Invalid value',
          () => {
            setEditingCell(null);
            editingValueRef.current = null;
            if (sendStopEditing) {
              const payload = extractEditingPayload(
                itemId,
                columnKey,
                groupedRowsRef.current,
              );
              if (payload) sendStopEditing(payload);
            }
          },
        );
        return;
      }

      const areEqual = valuesAreEqual(newValue, oldValue, columnKey);

      if (areEqual) {
        setValidationError(null);
        if (sendStopEditing) {
          const payload = extractEditingPayload(
            itemId,
            columnKey,
            groupedRowsRef.current,
          );
          if (payload) sendStopEditing(payload);
        }
        setEditingCell(null);
        editingValueRef.current = null;
        return;
      }

      if (!bypassMultiEditCheck && !skipMultiEditCheckRef.current) {
        const isMultiEditField = MULTI_EDIT_FIELDS.includes(columnKey);
        const currentSelected = selectedItemsRef.current;
        const hasMultipleSelected =
          currentSelected && currentSelected.length > 1;
        const currentItemIsSelected = currentSelected?.includes(itemId);

        if (isMultiEditField && hasMultipleSelected && currentItemIsSelected) {
          let canMultiEdit = true;

          let hasJobEntryRow = false;
          let hasNonJobEntryRow = false;
          (currentSelected as string[]).forEach((selId) => {
            for (const group of currentGroupedRows) {
              for (const jobCodeGroup of group.jobCodeGroups) {
                const foundItem = jobCodeGroup.items.find(
                  (item) => getItemId(item) === selId,
                );
                if (foundItem) {
                  if (foundItem.jobEntryId) {
                    hasJobEntryRow = true;
                  } else {
                    hasNonJobEntryRow = true;
                  }
                  break;
                }
              }
            }
          });
          if (hasJobEntryRow && hasNonJobEntryRow) {
            canMultiEdit = false;
          }

          if (
            canMultiEdit &&
            (columnKey === 'pickupLocation' || columnKey === 'containerStatus')
          ) {
            const selectedTradeTypes = new Set<string | null | undefined>();
            (currentSelected as string[]).forEach((selId) => {
              for (const group of currentGroupedRows) {
                for (const jobCodeGroup of group.jobCodeGroups) {
                  const foundItem = jobCodeGroup.items.find(
                    (item) => getItemId(item) === selId,
                  );
                  if (foundItem) {
                    if (foundItem.jobEntryId) {
                      selectedTradeTypes.add(foundItem.tradeType ?? null);
                    }
                    break;
                  }
                }
              }
            });
            canMultiEdit = selectedTradeTypes.size <= 1;
          }

          if (canMultiEdit && columnKey === 'deliveryDate') {
            const loadingDays = new Set<string>();
            (currentSelected as string[]).forEach((selId) => {
              for (const group of currentGroupedRows) {
                for (const jobCodeGroup of group.jobCodeGroups) {
                  const foundItem = jobCodeGroup.items.find(
                    (item) => getItemId(item) === selId,
                  );
                  if (foundItem) {
                    if (foundItem.jobEntryId && foundItem.loadingDeliveryDate) {
                      loadingDays.add(
                        formatDateOnly(foundItem.loadingDeliveryDate),
                      );
                    }
                    break;
                  }
                }
              }
            });
            canMultiEdit = loadingDays.size <= 1;
          }

          if (canMultiEdit) {
            if (
              multiEditConfirmationRef.current &&
              multiEditConfirmationRef.current.itemId === itemId &&
              multiEditConfirmationRef.current.columnKey === columnKey
            ) {
              return;
            }

            setValidationError(null);
            const cellElement = document.querySelector(
              `[data-item-id="${itemId}"][data-column="${columnKey}"]`,
            ) as HTMLElement;
            if (!cellElement) {
              return;
            }
            const rect = cellElement.getBoundingClientRect();
            const position = {
              top: rect.bottom + window.scrollY + 5,
              left: rect.left + window.scrollX,
            };
            setMultiEditConfirmation({
              itemId,
              columnKey,
              newValue,
              position,
            });
            return;
          }
        }
      }

      setValidationError(null);

      if (NOTIFY_ONLY_COLUMNS.includes(columnKey)) {
        const backendPayload = transformCellUpdateForBackend(
          { itemId, columnKey, newValue, oldValue },
          rowData,
        );
        if (sendCellUpdateNotify) {
          if (Array.isArray(backendPayload)) {
            backendPayload.forEach((payload) => sendCellUpdateNotify(payload));
          } else {
            sendCellUpdateNotify(backendPayload);
          }
        }
        if (sendStopEditing) {
          const payload = extractEditingPayload(
            itemId,
            columnKey,
            groupedRowsRef.current,
          );
          if (payload) sendStopEditing(payload);
        }
        setEditingCell(null);
        editingValueRef.current = null;
        return;
      }

      markFieldAsSaved(columnKey);

      const pendingKey = `${itemId}-${columnKey}`;
      pendingEdits.set(pendingKey, () => {}, TIMING.PENDING_EDIT_TIMEOUT);

      const editRecord: RecentEdit = {
        itemId,
        columnKey,
        newValue,
        timestamp: Date.now(),
      };
      setRecentEdits((prev) => [...prev, editRecord]);

      setTimeout(() => {
        setRecentEdits((prev) =>
          prev.filter(
            (edit) =>
              Date.now() - edit.timestamp <
              TIMING.BACKEND_RESPONSE_TIMEOUT + 5000,
          ),
        );
      }, TIMING.BACKEND_RESPONSE_TIMEOUT + 5000);

      updateRowsWithFormattedValue(setGroupedRows, itemId, columnKey, newValue);

      setSavingCells((prev) => [
        ...prev.filter(
          (cell) => !(cell.itemId === itemId && cell.columnKey === columnKey),
        ),
        { itemId, columnKey, status: 'loading', oldValue },
      ]);

      const backendPayload = transformCellUpdateForBackend(
        { itemId, columnKey, newValue, oldValue },
        rowData,
      );
      sendUpdateToBackend(
        itemId,
        columnKey,
        backendPayload,
        setGroupedRows,
        isDriverBatchedSelect,
      );

      setEditingCell(null);
      editingValueRef.current = null;
      markCellAsEdited(itemId, columnKey);
    },
    [
      validationTimeouts,
      sendStopEditing,
      showValidationError,
      sendCellUpdateNotify,
      markFieldAsSaved,
      pendingEdits,
      updateRowsWithFormattedValue,
      sendUpdateToBackend,
      markCellAsEdited,
    ],
  );

  const handleCellCancel = useCallback(() => {
    const currentEditingCell = editingCellRef.current;

    validationTimeouts.clear('validation');
    setValidationError(null);
    setEditingCell(null);
    editingValueRef.current = null;

    if (currentEditingCell) {
      if (sendStopEditing) {
        const payload = extractEditingPayload(
          currentEditingCell.itemId,
          currentEditingCell.columnKey,
          groupedRowsRef.current,
        );
        if (payload) sendStopEditing(payload);
      }

      markCellAsEdited(currentEditingCell.itemId, currentEditingCell.columnKey);
    }
  }, [sendStopEditing, validationTimeouts, markCellAsEdited]);

  const handleMultiEditConfirm = useCallback(
    (setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>) => {
      const currentMultiEdit = multiEditConfirmationRef.current;
      const currentSelectedItems = selectedItemsRef.current;
      if (!currentMultiEdit || !currentSelectedItems) return;

      const { itemId, columnKey, newValue } = currentMultiEdit;
      const currentGroupedRows = groupedRowsRef.current;

      const { rowData, oldValue } = findRowAndOldValue(
        currentGroupedRows,
        itemId,
        columnKey,
      );

      const isItemEditable = (foundItem: any) => {
        const isWeighingOnImport =
          columnKey === 'weighing' && foundItem.tradeType === 'IMPORT';
        const isPickupLocationCaptured =
          columnKey === 'pickupLocation' && foundItem.captured;
        const isRecordAccessible = foundItem.jobEntryId
          ? canEditAllRecordsRef.current ||
            (hasOwnerEditPrivilegeRef.current &&
              foundItem.ownerId != null &&
              ownerIdsRef.current?.has(foundItem.ownerId))
          : canEditAllShipmentPlansRef.current ||
            (hasOwnerEditShipmentPlansPrivilegeRef.current &&
              foundItem.ownerId != null &&
              ownerIdsRef.current?.has(foundItem.ownerId));
        return (
          !isWeighingOnImport &&
          !isPickupLocationCaptured &&
          !!isRecordAccessible
        );
      };

      const { jobEntryIds, shipmentPlanContainerIds, editableItemIds } =
        findEditableSelectedItems(
          currentGroupedRows,
          currentSelectedItems,
          isItemEditable,
        );

      if (jobEntryIds.length === 0 && shipmentPlanContainerIds.length === 0)
        return;

      if (NOTIFY_ONLY_COLUMNS.includes(columnKey)) {
        const singleNotifyPayload = transformCellUpdateForBackend(
          { itemId, columnKey, newValue, oldValue },
          rowData,
        );

        const multiEditNotifyPayload = buildMultiEditPayload(
          singleNotifyPayload,
          jobEntryIds,
          shipmentPlanContainerIds,
        );

        if (sendCellUpdateNotify) {
          if (Array.isArray(multiEditNotifyPayload)) {
            multiEditNotifyPayload.forEach((payload) =>
              sendCellUpdateNotify(payload),
            );
          } else {
            sendCellUpdateNotify(multiEditNotifyPayload);
          }
        }

        setMultiEditConfirmation(null);
        setEditingCell(null);
        editingValueRef.current = null;

        skipMultiEditCheckRef.current = true;
        setTimeout(() => {
          skipMultiEditCheckRef.current = false;
        }, 500);
        return;
      }

      markFieldAsSaved(columnKey);

      editableItemIds.forEach((selectedId) => {
        updateRowsWithFormattedValue(
          setGroupedRows,
          selectedId,
          columnKey,
          newValue,
        );
      });

      setSavingCells((prev) => [
        ...prev.filter(
          (cell) =>
            !editableItemIds.includes(cell.itemId) ||
            cell.columnKey !== columnKey,
        ),
        ...editableItemIds.map((selectedId) => ({
          itemId: selectedId,
          columnKey,
          status: 'loading' as const,
          oldValue,
        })),
      ]);

      const singlePayload = transformCellUpdateForBackend(
        { itemId, columnKey, newValue, oldValue },
        rowData,
      );

      const multiEditPayload = buildMultiEditPayload(
        singlePayload,
        jobEntryIds,
        shipmentPlanContainerIds,
      );

      const multiEditTimeoutKey = `multi-${itemId}-${columnKey}`;
      editableItemIds.forEach((selectedId) => {
        multiEditTimeoutKeys.current.set(
          `${selectedId}-${columnKey}`,
          multiEditTimeoutKey,
        );
      });

      scheduleMultiEditFailureTimeout(
        multiEditTimeoutKey,
        editableItemIds,
        columnKey,
        () => {
          editableItemIds.forEach((selectedId) => {
            setGroupedRows((prevRows) =>
              updateGroupedRows(prevRows, selectedId, columnKey, oldValue),
            );
          });
        },
      );

      if (sendCellUpdate) {
        if (Array.isArray(multiEditPayload)) {
          multiEditPayload.forEach((payload) => sendCellUpdate(payload));
        } else {
          sendCellUpdate(multiEditPayload);
        }
      }

      multiEditJustConfirmedRef.current = { itemId, columnKey };
      setTimeout(() => {
        multiEditJustConfirmedRef.current = null;
      }, 500);

      setMultiEditConfirmation(null);
      setEditingCell(null);
      editingValueRef.current = null;
      markCellAsEdited(itemId, columnKey);

      skipMultiEditCheckRef.current = true;
      setTimeout(() => {
        skipMultiEditCheckRef.current = false;
      }, 500);
    },
    [
      markFieldAsSaved,
      sendCellUpdateNotify,
      updateRowsWithFormattedValue,
      scheduleMultiEditFailureTimeout,
      sendCellUpdate,
      markCellAsEdited,
    ],
  );

  const handleMultiEditCancel = useCallback(
    (setGroupedRows: React.Dispatch<React.SetStateAction<GroupedItems[]>>) => {
      const currentMultiEdit = multiEditConfirmationRef.current;
      if (!currentMultiEdit) return;

      const { itemId, columnKey, newValue } = currentMultiEdit;
      const currentGroupedRows = groupedRowsRef.current;

      setMultiEditConfirmation(null);

      skipMultiEditCheckRef.current = true;
      setTimeout(() => {
        skipMultiEditCheckRef.current = false;
      }, 500);

      handleCellSave(
        itemId,
        columnKey,
        newValue,
        currentGroupedRows,
        setGroupedRows,
        true,
      );
    },
    [handleCellSave],
  );

  const handleMultiEditClickOutside = useCallback(() => {
    const currentMultiEdit = multiEditConfirmationRef.current;
    if (!currentMultiEdit) return;

    setMultiEditConfirmation(null);
    setEditingCell(null);
    editingValueRef.current = null;

    skipMultiEditCheckRef.current = true;
    setTimeout(() => {
      skipMultiEditCheckRef.current = false;
    }, 500);

    if (sendStopEditing) {
      const payload = extractEditingPayload(
        currentMultiEdit.itemId,
        currentMultiEdit.columnKey,
        groupedRowsRef.current,
      );
      if (payload) sendStopEditing(payload);
    }
  }, [sendStopEditing]);

  const handleCellUpdateResponse = useCallback(
    (
      itemId: string,
      columnKey: string,
      success: boolean,
      newValue?: any,
      errorMessage?: string,
      setGroupedRows?: React.Dispatch<React.SetStateAction<GroupedItems[]>>,
      skipLoadingCheck = false,
      fromOtherUser = false,
    ) => {
      const pendingKey = `${itemId}-${columnKey}`;
      const isPending = pendingEdits.has(pendingKey);

      const itemKey = `${itemId}-${columnKey}`;
      const multiEditTimeoutKey = multiEditTimeoutKeys.current.get(itemKey);
      if (multiEditTimeoutKey) {
        backendTimeouts.clear(multiEditTimeoutKey);
        const keysToDelete: string[] = [];
        multiEditTimeoutKeys.current.forEach((value, key) => {
          if (value === multiEditTimeoutKey) keysToDelete.push(key);
        });
        keysToDelete.forEach((key) => multiEditTimeoutKeys.current.delete(key));
      } else {
        backendTimeouts.clear(itemKey);
      }

      if (isPending) {
        pendingEdits.clear(pendingKey);
      }

      const cellIsLoading = savingCellsRef.current.some(
        (cell) =>
          cell.itemId === itemId &&
          cell.columnKey === columnKey &&
          cell.status === 'loading',
      );

      let matchingEdit =
        success && newValue !== undefined
          ? recentEditsRef.current.find(
              (edit) =>
                edit.columnKey === columnKey &&
                valuesMatchForCorrelation(edit.newValue, newValue, columnKey),
            )
          : null;

      if (!matchingEdit && success && newValue !== undefined) {
        matchingEdit =
          recentEditsRef.current.find(
            (edit) =>
              edit.itemId === itemId && edit.columnKey === 'customsClearance',
          ) || null;
      }

      if (matchingEdit) {
        setTimeout(() => {
          setRecentEdits((prev) =>
            prev.filter((edit) => edit !== matchingEdit),
          );
        }, 1000);
      }

      const actualFromOtherUser =
        isPending || cellIsLoading || matchingEdit ? false : fromOtherUser;

      if (!success && setGroupedRows) {
        setSavingCells((prev) => {
          const savingCell = prev.find(
            (cell) =>
              cell.itemId === itemId &&
              cell.columnKey === columnKey &&
              cell.status === 'loading',
          );

          if (savingCell?.oldValue !== undefined) {
            setGroupedRows((prevRows) =>
              updateGroupedRows(
                prevRows,
                itemId,
                columnKey,
                savingCell.oldValue,
              ),
            );
          }

          return prev;
        });

        if (errorMessage && !actualFromOtherUser) {
          const translatedError =
            errorMessage === 'VALIDATION_ERROR' &&
            columnKey === 'containerNumber'
              ? ERRORS.containerNumberError
              : errorMessage ===
                'Validation error: Container is captured (import)'
              ? ERRORS.capturedError
              : errorMessage ===
                'Validation error: Container already present in job'
              ? ERRORS.containerNumberError
              : errorMessage ===
                'Someone else modified this record. Please refresh the page and try again.'
              ? ERRORS.elseModifiedError
              : ERRORS.systemError;
          appToast.error(translatedError);
        }
      } else if (success && setGroupedRows && newValue !== undefined) {
        const isCurrentlyLoading = savingCellsRef.current.some(
          (cell) =>
            cell.itemId === itemId &&
            cell.columnKey === columnKey &&
            cell.status === 'loading',
        );

        if (columnKey === 'loadingDeliveryDate') {
          markDateChangeAccepted(itemId);
        }

        if (
          (!isCurrentlyLoading && columnKey !== 'containerStatus') ||
          columnKey === 'loadingDeliveryDate'
        ) {
          const normalizedValue =
            columnKey === 'truckerRate'
              ? normalizeTruckerRate(newValue)
              : newValue;

          updateRowsWithFormattedValue(
            setGroupedRows,
            itemId,
            columnKey,
            normalizedValue,
          );
        }
      }

      setSavingCells((prev) => {
        const isInLoadingState = savingCellsRef.current.some(
          (cell) =>
            cell.itemId === itemId &&
            cell.columnKey === columnKey &&
            cell.status === 'loading',
        );

        if (isInLoadingState || skipLoadingCheck) {
          const filteredCells = prev.filter(
            (cell) => !(cell.itemId === itemId && cell.columnKey === columnKey),
          );

          const status = success
            ? actualFromOtherUser
              ? ('success-other' as const)
              : ('success' as const)
            : actualFromOtherUser
            ? ('success-other' as const)
            : ('error' as const);

          return [...filteredCells, { itemId, columnKey, status }];
        }

        return prev;
      });

      fadeAndRemoveSavingCells([itemId], columnKey);

      if (sendStopEditing && !actualFromOtherUser) {
        const payload = extractEditingPayload(
          itemId,
          columnKey,
          groupedRowsRef.current,
        );
        if (payload) sendStopEditing(payload);
      }
    },
    [
      sendStopEditing,
      pendingEdits,
      backendTimeouts,
      markDateChangeAccepted,
      updateRowsWithFormattedValue,
      fadeAndRemoveSavingCells,
    ],
  );

  const cleanupEditingState = useCallback(() => {
    isCleanedUp.current = true;

    const currentEditing = editingCellRef.current;
    if (currentEditing && sendStopEditing) {
      const payload = extractEditingPayload(
        currentEditing.itemId,
        currentEditing.columnKey,
        groupedRowsRef.current,
      );
      if (payload) sendStopEditing(payload);
    }
    validationTimeouts.clearAll();
    setValidationError(null);
    setEditingCell(null);
    editingValueRef.current = null;
  }, [sendStopEditing, validationTimeouts]);

  const isPendingEdit = useCallback(
    (itemId: string, columnKey: string) => {
      const pendingKey = `${itemId}-${columnKey}`;
      return pendingEdits.has(pendingKey);
    },
    [pendingEdits],
  );

  useEffect(
    () => () => {
      validationTimeouts.clearAll();
      savedFieldTimeouts.clearAll();
      pendingEdits.clearAll();
      backendTimeouts.clearAll();
      setRecentEdits([]);
    },
    [validationTimeouts, savedFieldTimeouts, pendingEdits, backendTimeouts],
  );

  const clearDateChangedRows = useCallback(() => {
    setDateChangedRows(new Map());
  }, []);

  const isFieldRecentlySaved = useCallback(
    (columnKey: string) => recentlySavedFieldsRef.current.includes(columnKey),
    [],
  );

  return {
    editingCell,
    editingValue: editingValueRef.current,
    savingCells,
    recentlyEditedCell,
    validationError,
    recentlySavedFields,
    dateChangedRows,
    multiEditConfirmation,
    acceptMultiEditConfirmation,
    setEditingValue,
    handleCellEdit,
    handleCellSave,
    handleCellCancel,
    handleCellUpdateResponse,
    cleanupEditingState,
    isPendingEdit,
    isFieldRecentlySaved,
    clearDateChangedRows,
    handleDateChangeNotify,
    handleAcceptDateChange,
    seedDateChangedRowsFromItems,
    handleAcceptMultiEditConfirm,
    handleAcceptMultiEditCancel,
    handleAcceptMultiEditClickOutside,
    handleMultiEditConfirm,
    handleMultiEditCancel,
    handleMultiEditClickOutside,
    setMultiEditConfirmation,
  };
};
